import React, { useState } from 'react';
import { SvgIcon } from '../../Icon';
import toastMessage from '../../../utils/toastMessage';
import {
  ActionButton,
  ButtonContainer,
  ButtonGrid,
  Content,
  PopoverContainer,
  Section,
} from '../EntityPermissionsWidgetPopover/EntityPermissionsWidgetPopoverUI';
import EmbedCodeContent from '../EmbedCodeContent/EmbedCodeContent';
import styled from 'styled-components';
import { useUserSettingsSelector } from '../../../redux/selectorHooks';
import { useChartContext } from '../../Chart/ChartContext';
import { generateEmbedCode } from '../../../utils/embed';
import { buttonClicked } from '../../../utils/analytics/events/button/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { useExternalEmbedFeature } from '../../../utils/hooks';
import { AnalysisStatus, QueryType } from '../../../utils/apollo/graphqlSchema';
import { CHART_TYPES } from '../../Chart/constants/chartTypes';
import copyTextToClipboard from '../../../utils/copyTextToClipboard';

interface IProps {
  reportLink: string;
}

const Container = styled.div`
  position: relative;
  min-width: 300px;
`;

const ReportEntityPermissionsWidgetPopover = ({ reportLink }: IProps) => {
  const { track } = useAnalytics();
  const {
    metadata: { exportUrl, status, queryType },
    settings: { chartSettings },
  } = useChartContext();
  const { chartType } = chartSettings;
  const jupyterHost = useUserSettingsSelector('jupyterHost');
  const isEmbedFeatureEnabled = useExternalEmbedFeature();
  const [showEmbedContent, setShowEmbedContent] = useState(false);

  const embedCode = generateEmbedCode(
    `${window.location.origin}/embed${reportLink}`,
    {
      title: 'Kubit Report',
    },
  );

  const onEmbedCodeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowEmbedContent(true);
  };

  const onDownloadCsvClick = () => {
    if (exportUrl) {
      track(buttonClicked({ context: 'reports', action: 'export_csv' }));
      window.location.assign(exportUrl);
      toastMessage({
        message: 'Exporting report data to CSV!',
        type: 'info',
      });
    }
  };

  const onDownloadJupyterClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const link = `${jupyterHost}/kubit?url=${exportUrl}`;

    await copyTextToClipboard(link);
    track(buttonClicked({ context: 'reports', action: 'export_jupyter' }));
    window.open(link, '_blank');
    toastMessage({
      message: 'Report data exported to Jupyter.',
      type: 'info',
    });
  };

  const shouldShowJupyterButton =
    status === AnalysisStatus.Success &&
    exportUrl &&
    chartType !== CHART_TYPES.PATH &&
    chartType !== CHART_TYPES.PREDICTION &&
    chartType !== CHART_TYPES.COHORT_SAMPLE &&
    chartType !== CHART_TYPES.DATA_TABLE;

  const shouldShowCsvButton =
    status === AnalysisStatus.Success &&
    exportUrl &&
    chartType !== CHART_TYPES.PATH &&
    chartType !== CHART_TYPES.PREDICTION &&
    queryType !== QueryType.DataTable;

  return (
    <Container>
      <PopoverContainer>
        <Content>
          <Section>
            <ButtonGrid>
              {isEmbedFeatureEnabled && (
                <ButtonContainer onClick={onEmbedCodeClick}>
                  <ActionButton isOutlined={true}>
                    <SvgIcon name="code-regular" width={24} height={24} />
                  </ActionButton>
                  Embed code
                </ButtonContainer>
              )}
              {shouldShowJupyterButton && (
                <ButtonContainer onClick={onDownloadJupyterClick}>
                  <ActionButton isOutlined={true}>
                    <SvgIcon name="export-to-jupyter" width={24} height={24} />
                  </ActionButton>
                  Jupyter
                </ButtonContainer>
              )}
              {shouldShowCsvButton && (
                <ButtonContainer onClick={onDownloadCsvClick}>
                  <ActionButton isOutlined={true}>
                    <SvgIcon name="export-to-csv" width={24} height={24} />
                  </ActionButton>
                  Data to CSV
                </ButtonContainer>
              )}
            </ButtonGrid>
          </Section>
        </Content>
      </PopoverContainer>

      {showEmbedContent && (
        <EmbedCodeContent
          trackContext="reports"
          embedCode={embedCode}
          onClose={() => setShowEmbedContent(false)}
        />
      )}
    </Container>
  );
};

export default ReportEntityPermissionsWidgetPopover;
